import React, { useEffect, useState } from 'react'
import { Button, Card, Grid, makeStyles, Typography } from '@material-ui/core'
import { Form, Formik } from 'formik'
import { speakerFormSchema } from 'schemas/speaker'
import CustomTextField from 'components/Form/CustomTextField'
import {
  Collection,
  SubjectStatus,
  SPEAKER_INFO_TEXT,
  SubjectInfoMessages,
  subjectReason
} from 'constants/index'
import { Box } from '@mui/material'
import ProgressButton from 'components/ProgressButton'
import { resetSubject, submitSubjectData, verifyGoogleRecaptcha } from 'store/subject'
import { useDispatch, useSelector } from 'react-redux'
import Recaptcha from 'components/Recaptcha'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router-dom'
import SubjectSpecificDetails from 'components/SubjectSpecificDetails'
import { AssociateSubjectPollsToSpeaker } from 'store/polling'
import { fetchSubjectsTalks } from 'store/talk'
import { colors } from 'utils/colors'
import { Talks } from 'views/admin/subjects/HTMLForms/TalksListing'
import AvatarPicker from 'components/AvatarPicker'
import { updateSpeakerProfile } from 'store/speaker'
import InfoIconAlongText from 'components/InfoIconAlongText'
import ReactQuill from 'react-quill'
import { modules } from 'views/admin/settings/SettingForm/TemplateForm'
import ErrorMessage from 'components/ErrorMessage'

const Speaker = (props) => {
  const { subject, submitTitle, handleSubmit, hideForm, reason } = props
  const classes = useStyles()
  const history = useHistory()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [recaptchaCode, setRecaptchaCode] = useState('')
  const [isRecaptchaValid, setIsRecaptchaValid] = useState(false)
  const { conference } = useSelector((state) => state.conference)

  useEffect(() => {
    if (reason === subjectReason.forApproval) {
      dispatch(fetchSubjectsTalks(subject?.id))
    }
  }, [dispatch, subject?.id, subjectReason.forApproval])

  const handleChangeRecaptcha = (value) => {
    setRecaptchaCode(value)
    if (value) {
      setIsRecaptchaValid(true)
    }
  }
  const handleExpired = () => {
    setIsRecaptchaValid(false)
  }
  const handleUpload = async (name) => {
    await dispatch(submitSubjectData(subject?.id, { ...subject, imageName: name }))
    subject?.speakerId && (await updateSpeakerProfile(subject?.speakerId, name))
  }

  const handleDelete = async () => {
    await dispatch(submitSubjectData(subject?.id, { ...subject, imageName: null }))
    subject?.speakerId && (await updateSpeakerProfile(subject?.speakerId, null))
  }

  const goBack = () => {
    dispatch(resetSubject())
    history.goBack()
  }

  const initialValues = {
    email: subject?.email ?? '',
    fullName: subject?.fullName ?? '',
    company: subject?.company ?? '',
    position: subject?.position ?? '',
    bio: subject?.bio ?? '',
    havePoll: subject?.havePoll ?? 'no',
    websiteUrl: subject?.websiteUrl ?? ''
  }

  return (
    <Card className={reason === subjectReason.registration ? classes.card : ''} elevation={0}>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={async (values) => {
          try {
            setLoading(true)
            if (reason === subjectReason.registration) {
              const isVerified = await dispatch(verifyGoogleRecaptcha(recaptchaCode))
              if (isVerified) {
                await handleSubmit(values)
              } else {
                toast.error('reCAPTCHA verification is failed.')
              }
            } else {
              const speakerId = await handleSubmit(values, reason)
              speakerId && (await dispatch(AssociateSubjectPollsToSpeaker(subject?.id, speakerId)))
            }
            setLoading(false)
          } catch (e) {
            // do nothing
          } finally {
            setLoading(false) // do nothing but stops the loading
          }
        }}
        validationSchema={speakerFormSchema}>
        {({ errors, values, setFieldValue, touched }) => (
          <Form>
            <Box className={classes.headBar}>
              <Typography variant="h4">
                {reason === subjectReason.forApproval
                  ? 'Review Speaker'
                  : reason === subjectReason.afterApproval
                  ? 'Edit Speaker'
                  : 'Register as a Speaker'}
              </Typography>
              {conference?.speakerCanUploadTalks &&
                reason === subjectReason.registration &&
                (subject?.status === SubjectStatus.PENDING_DATA_REVIEW ||
                  subject?.status === SubjectStatus.APPROVED) && (
                  <Button
                    size="medium"
                    color="primary"
                    variant="contained"
                    onClick={() => hideForm && hideForm()}>
                    View Talks
                  </Button>
                )}
              {reason !== subjectReason.registration && (
                <Box sx={{ display: 'flex', gap: '1rem' }}>
                  <Button size="small" color="primary" variant="contained" onClick={goBack}>
                    back
                  </Button>
                  <ProgressButton
                    type="submit"
                    label={submitTitle}
                    inProgress={loading}
                    disabled={loading}
                  />
                </Box>
              )}
            </Box>

            {reason !== subjectReason.registration && (
              <Box p={1} border={1} sx={{ mb: '2rem' }}>
                <Box
                  p={1}
                  sx={{
                    backgroundColor: '#ECECEC',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}>
                  <Box className={classes.singleInfo}>
                    <Typography className={classes.label}>Conference Name:</Typography>
                    <Typography>{conference?.confName}</Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {reason === subjectReason.registration && (
              <SubjectSpecificDetails infoText={SubjectInfoMessages.SPEAKER} />
            )}

            <Grid container spacing={3} className={classes.form}>
              <Grid item xs={12} lg={4}>
                <CustomTextField
                  label="Email"
                  name="email"
                  type="text"
                  disabled={reason === subjectReason.registration}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="Full Name" name="fullName" type="text" />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="Institution" name="company" type="text" />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="Position" name="position" type="text" />
              </Grid>
              <Grid item xs={12} lg={8}>
                <CustomTextField label="Website URL" name="websiteUrl" type="text" />
              </Grid>
              <Grid item xs={12} lg={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label className={classes.labelA}>
                    Bio
                    <InfoIconAlongText infoText={SPEAKER_INFO_TEXT.bio} />
                  </label>
                  <ReactQuill
                    theme="snow"
                    modules={modules}
                    value={values.bio}
                    onChange={(value) => setFieldValue('bio', value)}
                    placeholder="Write bio here..."
                  />
                  <ErrorMessage message={touched?.bio && errors?.bio} />
                </Box>
              </Grid>

              {conference?.speakerCanUploadTalks && reason !== subjectReason.registration && (
                <Grid item xs={12}>
                  <Box className={classes.talkSection}>
                    <Typography className={classes.cardHeading}>Speaker Talks</Typography>
                    <Talks forApproval={true} subjectId={subject?.id} confId={conference?.id} />
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} lg={6}>
                <AvatarPicker
                  title="Profile Picture"
                  subTitle="Allowed Format: png/jpg ; Dimensions : 320x320"
                  path={Collection.SPEAKERS}
                  onUpload={handleUpload}
                  onDelete={handleDelete}
                  imageName={subject?.imageName ? subject?.imageName : null}
                  resetImage={!subject?.imageName}
                  conferenceId={subject?.conferenceId}
                  infoText={SPEAKER_INFO_TEXT.profile}
                  isSubject={true}
                  reason={reason}
                  imageProps={{ style: { objectFit: 'cover', objectPosition: '50% 12%' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box className={classes.buttonBox}>
                  {reason === subjectReason.registration && (
                    <Recaptcha handleChange={handleChangeRecaptcha} onExpired={handleExpired} />
                  )}
                  <Box sx={{ display: 'flex', gap: '1rem' }}>
                    {reason !== subjectReason.registration && (
                      <Button onClick={goBack}>Cancel</Button>
                    )}
                    <ProgressButton
                      type="submit"
                      label={submitTitle}
                      size="large"
                      inProgress={loading}
                      disabled={
                        loading || (reason === subjectReason.registration && !isRecaptchaValid)
                      }
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Card>
  )
}

export default Speaker

const useStyles = makeStyles({
  labelA: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    color: '#606060',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginLeft: '0.2rem'
  },
  headBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0rem 0rem 2rem 0rem'
  },
  card: {
    padding: '1rem',
    border: '1px solid #e0e0e0'
  },
  header: {
    fontWeight: 'bold'
  },
  form: {
    marginTop: '2rem'
  },
  radioGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  radioLabel: {
    fontWeight: 600,
    fontSize: '16px',
    color: '#2f2f2f'
  },
  singleInfo: {
    display: 'flex',
    gap: '2rem'
  },
  label: {
    minWidth: '132px',
    fontSize: '15px',
    fontWeight: '600'
  },
  infoBox: {
    padding: '10px 16px',
    borderRadius: '8px',
    backgroundColor: '#e0f7fa'
  },
  warningModalHeader: {
    padding: '20px 20px 10px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  warningTitle: {
    fontSize: '16px',
    fontWeight: 'bold'
  },
  actions: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginBottom: '10px'
  },
  iconButton: {
    padding: '8px'
  },
  buttonBox: {
    marginTop: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '1rem'
  },
  cardHeading: {
    color: colors.indigo,
    fontSize: '18px'
  },
  talkSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    border: '1px solid #bdbdbd',
    borderRadius: '10px',
    padding: '16px'
  },
  talkCard: {
    padding: '10px 30px',
    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  talks: { marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
  notFound: {
    textAlign: 'center',
    fontSize: '14px'
  },
  success: {
    backgroundColor: '#69f0ae'
  },
  warning: {
    backgroundColor: '#ffc107'
  }
})
